import { type ReactNode, useMemo } from 'react';
import { DynamicIcon } from 'src/components/DynamicIcon/dynamic-icon';
import {
  SelectMenu,
  type SelectMenuOption,
  type SelectMenuTriggerState,
} from 'src/components/SelectMenu';
import { WorkflowStepResultKind } from 'src/gql/graphql';
import { WorkflowTriggerType } from 'src/graphql/entities/workflow/workflow.trigger';
import {
  getAddableStepTypes,
  type WorkflowInsertionPoint,
  WorkflowNodeType,
} from 'src/pages/Settings/workflows/utils/workflow-definition';
import { createStepId } from 'src/pages/Settings/workflows/utils/workflow-definition-edits';
import { getStepIcon } from 'src/pages/Settings/workflows/utils/workflow-helpers';
import { useWorkflowEditor } from 'src/pages/Settings/workflows/workflow-editor-context';

/** Sections of the picker, from what the backend says each step produces. */
const GROUP_BY_RESULT: Record<WorkflowStepResultKind, string> = {
  [WorkflowStepResultKind.Twiml]: 'Respond',
  [WorkflowStepResultKind.Scalar]: 'Decide',
  [WorkflowStepResultKind.Object]: 'Store',
  [WorkflowStepResultKind.None]: 'Other',
};
const GROUP_ORDER = Object.values(GROUP_BY_RESULT);

/**
 * The step-type picker behind a +: the shared SelectMenu, searchable and sectioned. The + itself
 * is the trigger, so the menu opens anchored to the connection or path end it was pressed on.
 */
export const WorkflowAddStepMenu = ({
  insertion,
  renderTrigger,
}: {
  insertion: WorkflowInsertionPoint;
  renderTrigger: (state: SelectMenuTriggerState) => ReactNode;
}) => {
  const { definition, catalog, dispatch, setOpenNodeId } = useWorkflowEditor();

  const trigger = definition.nodes.find(({ type }) => type === WorkflowNodeType.Trigger);
  const rawTriggerType = trigger?.data?.triggerType;
  const triggerType =
    typeof rawTriggerType === 'string' ? rawTriggerType : WorkflowTriggerType.InboundPhoneCall;

  const stepTypes = useMemo(
    () => getAddableStepTypes(catalog, triggerType),
    [catalog, triggerType],
  );

  const options = useMemo<SelectMenuOption[]>(
    () =>
      [...stepTypes]
        .sort(
          (a, b) =>
            GROUP_ORDER.indexOf(GROUP_BY_RESULT[a.result]) -
            GROUP_ORDER.indexOf(GROUP_BY_RESULT[b.result]),
        )
        .map((stepType) => ({
          value: stepType.type,
          title: stepType.label,
          description: stepType.description,
          icon: <DynamicIcon name={getStepIcon(stepType.type)} fontSize="small" />,
          group: GROUP_BY_RESULT[stepType.result],
          keywords: [stepType.type],
        })),
    [stepTypes],
  );

  const add = (type: string) => {
    const stepType = catalog.byType.get(type);
    if (!stepType) return;
    const nodeId = createStepId(definition, stepType.type);
    dispatch({ kind: 'insertStep', at: insertion, stepType, nodeId });
    // the new step opens straight away, ready for its fields
    setOpenNodeId(nodeId);
  };

  return (
    <SelectMenu
      label="Add a step"
      options={options}
      value={null}
      onChange={add}
      search
      searchPlaceholder="Find a step…"
      sectioned
      menuWidth="320px"
      renderTrigger={renderTrigger}
    />
  );
};
